import { useQuery } from '@tanstack/react-query';
import { useApiRequest } from '@/features/videos-common/api/client';
import { nextVideoKey } from './queryKeys';

/**
 * @typedef {import('./useHomepageVideos').HomeVideo} HomeVideo
 */

/**
 * Response of GET /videos/next. `next` omits homePosition.
 *
 * @typedef {Object} NextVideoResponse
 * @property {HomeVideo|null} next
 */

/**
 * GET /videos/next?videoId. The server resolves the user's audience and its
 * playlist; `{ next: null }` covers every "no Next" case. Results are cached
 * per video for the session. A failed request (400, 502, 503 or a network
 * error) is not retried; the caller just has no next video.
 *
 * @param {string|null|undefined} videoId - YouTube video id
 * @returns {{ nextVideo: HomeVideo|null }}
 */
export default function useNextVideo(videoId) {
  const request = useApiRequest();
  const enabled = Boolean(videoId);

  const query = useQuery({
    queryKey: nextVideoKey(videoId),
    queryFn: () => {
      const params = new URLSearchParams({ videoId });
      return request(`/videos/next?${params.toString()}`);
    },
    enabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    onError: (error) => {
      console.error('Failed to load the next video', error);
    }
  });

  if (!enabled || query.isError) return { nextVideo: null };
  return { nextVideo: query.data?.next ?? null };
}
